import {useEffect, useState} from "react";
import Header from "./Header";
import Loading from "./Loading";
import {deleteSubmissionCall, downloadManuscriptCall, fetchManuscript} from "../service/ManuscriptService";
import {
    Download, Files,
    Info, XCircle,
} from "lucide-react";
import blob from "../images/blob.png";
import {GiDropletSplash} from "react-icons/gi";
import {useNavigate} from "react-router-dom";
import {ExternalLink} from "lucide-react";
import {red, scoreDescriptions} from "../constants/Constants";
import {calculateOverallScore} from "../service/Operations";
import {MdOutlineHub} from "react-icons/md";
import DonutProgress from "./DonutProgress";

function Submission() {

    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    const [loading, setLoading] = useState(true);
    const [manuscript, setManuscript] = useState(null);
    const [error, setError] = useState(false);
    const [existsInS3, setExistsInS3] = useState(false);
    const [hasSimilarPapers, setHasSimilarPapers] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [infoDisplay, setInfoDisplay] = useState('none');
    const [infoDisplayNovelty, setInfoDisplayNovelty] = useState('none');
    const [infoDisplayMethodology, setInfoDisplayMethodology] = useState('none');
    const [infoDisplayImpact, setInfoDisplayImpact] = useState('none');
    const [infoDisplayOverall, setInfoDisplayOverall] = useState('none');

    useEffect(() => {
        setLoading(true);
        setError(false);
        setDownloaded(false);
        fetchManuscript(id, true)
            .then(response => {
                if (response.manuscript) {
                    setManuscript(response.manuscript);
                    if (response.existsInS3 != null) {
                        setExistsInS3(response.existsInS3);
                    }
                    if (response.hasSimilarPapers != null) {
                        setHasSimilarPapers(response.hasSimilarPapers);
                    }
                } else {
                    setError(true)
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [])

    function deleteSubmission(popup) {
        if (popup) {
            const confirm = window.confirm("Are you sure you want to delete this submission?");
            if (!confirm) {
                return;
            }
        }
        setLoading(true);
        setError(false);
        setDownloaded(false);
        deleteSubmissionCall(id, true)
            .then(response => {
                if (response.success) {
                    window.alert("Submission deleted!");
                    navigate("/submissions")
                } else {
                    window.alert("Failed to delete this submission");
                }
                setLoading(false);
            })
            .catch(() => {
                window.alert("Failed to delete this submission");
                setLoading(false);
            });
    }

    function downloadManuscript(id) {
        setLoading(true);
        setError(false);
        setDownloaded(false);
        downloadManuscriptCall(id, true)
            .then(({blob, disposition}) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");

                // ✅ Extract filename from Content-Disposition header
                let filename = "manuscript.pdf";

                if (disposition && disposition.includes("filename=")) {
                    filename = disposition
                        .split("filename=")[1]
                        .replace(/"/g, "") // remove quotes
                        .trim();
                }

                link.href = url;
                link.download = filename; // ✅ dynamically set filename
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                setLoading(false);
                setDownloaded(true);
            })

            .catch(() => {
                window.alert("Failed to download submission");
                setLoading(false);
            });
    }

    const displayBoxNovelty = (
        <div className={"submission-info-popup-score box-shadow fade-in"}
             style={{display: infoDisplayNovelty}}>
            <div>{scoreDescriptions.novelty}</div>
        </div>
    )

    const displayBoxMethodology = (
        <div className={"submission-info-popup-score box-shadow fade-in"}
             style={{display: infoDisplayMethodology}}>
            <div>{scoreDescriptions.methodology}</div>
        </div>
    )

    const displayBoxImpact = (
        <div className={"submission-info-popup-score box-shadow fade-in"}
             style={{display: infoDisplayImpact}}>
            <div>{scoreDescriptions.impact}</div>
        </div>
    )

    const displayBoxOverall = (
        <div className={"submission-info-popup-score-overall box-shadow fade-in"}
             style={{display: infoDisplayOverall}}>
            <div>{scoreDescriptions.overall}</div>
        </div>
    )


    return (
        <div>
            <Header page="submission"/>
            {error ?
                <div className={"submission-error"}>
                    <div>An error occurred while trying to load this submission.</div>
                    <div>Click <a href={"/submissions"}>here</a> to go back to the submissions page.</div>
                </div>
                :
                loading ?
                    <Loading/>
                    :
                    <div className="body">
                        {manuscript == null ?
                            null :
                            <div>
                                <div className="submission-card" style={{cursor: 'auto'}}>
                                    <div className={"submission-title"}>{
                                        (manuscript.title && manuscript.title.trim()) ||
                                        (manuscript.name ? manuscript.name.replace(/\.[^/.]+$/, "") : "Untitled")
                                    }
                                    </div>
                                    <div className="submission-authors">
                                        {manuscript.authors && manuscript.authors.trim() ? manuscript.authors : "Unknown authors"}
                                    </div>
                                    {(manuscript.status == null || manuscript.status === "inProcess" || manuscript.status === "new" || (manuscript.status === "error" && manuscript.attempts < 4)) ?
                                        <div className="submission-pending">
                                            **Pending Review**
                                        </div>
                                        :
                                        (manuscript.status === "error") ?
                                            <div className="submission-pending" style={{color: red}}>
                                                **Failed**
                                            </div>
                                            :
                                            null
                                    }
                                </div>
                                {manuscript.status === "error" && manuscript.attempts > 3 &&
                                    <div>
                                        <span>There was an issue processing this manuscript. Click </span>
                                        <span className={"user-account-link"} style={{color: red}}
                                             onClick={() => deleteSubmission(false)}
                                        >
                                            here
                                        </span>
                                        <span> to delete the submission, then upload the manuscript again.</span>
                                    </div>
                                }
                                {downloaded &&
                                    <div className={"certify-submitted-text"}
                                         style={{border: '2px solid green', width: 'max-content', padding: '0 5px'}}>
                                        <div>Manuscript Downloaded!</div>
                                    </div>
                                }
                                <table className="submission-table">
                                    <tbody>
                                    <tr style={{position: 'relative'}}>
                                        <td className={"submission-td-left"}>
                                            <div className={"novelty-score"}>
                                                <span>Score: </span>
                                                {manuscript.noveltyScore > 0 ?
                                                    <span>{calculateOverallScore(manuscript)}</span>
                                                    :
                                                    <span>?</span>
                                                }
                                            </div>
                                        </td>
                                        <td style={{position: 'relative'}}>
                                            {infoDisplayOverall === 'block' ?
                                                <XCircle size={'100%'} className={"score-icon-overall"}
                                                         onClick={() => {
                                                             setInfoDisplayOverall(infoDisplayOverall === 'none' ? 'block' : 'none');
                                                             setInfoDisplayNovelty('none');
                                                             setInfoDisplayMethodology('none');
                                                             setInfoDisplayImpact('none');
                                                         }}/>
                                                :
                                                <Info size={'100%'} className={"score-icon-overall"}
                                                      onClick={() => {
                                                          setInfoDisplayOverall(infoDisplayOverall === 'none' ? 'block' : 'none');
                                                          setInfoDisplayNovelty('none');
                                                          setInfoDisplayMethodology('none');
                                                          setInfoDisplayImpact('none');
                                                      }}/>
                                            }
                                            {displayBoxOverall}
                                        </td>

                                        <td className="submission-td-right">
                                            <div className={"blob"}>
                                                {/* left single */}
                                                <img style={{width: 100, display: "block"}} src={blob} alt="blob"/>
                                                {/*<div className={"submission-info-popup box-shadow fade-in"}*/}
                                                {/*     style={{display: infoDisplay}}>*/}
                                                {/*    <div style={{fontWeight: 700, fontSize: 'calc(1em + 2px)'}}>Key*/}
                                                {/*        Drivers:*/}
                                                {/*    </div>*/}
                                                {/*    <ul>*/}
                                                {/*        <li>*/}
                                                {/*            <span>Sparse Cluster </span>*/}
                                                {/*            <span>(topic in a low-density research zone)</span>*/}
                                                {/*        </li>*/}
                                                {/*        <li>*/}
                                                {/*            <span>Citation Outlier </span>*/}
                                                {/*            <span>(few shared citations with literature)</span>*/}
                                                {/*        </li>*/}
                                                {/*        <li>*/}
                                                {/*            <span style={{fontWeight: 700}}>Rare Keywords </span>*/}
                                                {/*            <span>(terms not widely used in related fields)</span>*/}
                                                {/*        </li>*/}
                                                {/*    </ul>*/}
                                                {/*</div>*/}
                                                {/* right column of two */}
                                                {/*<div style={{display: "flex", flexDirection: "column", gap: 60}}>*/}
                                                {/*    /!*<Eraser size={30} className={"submission-icon"}*!/*/}
                                                {/*    /!*        onClick={() => navigate("/suggested-editorial-action?id=" + id)}/>*!/*/}
                                                {/*    {infoDisplay === 'block' ?*/}
                                                {/*        <XCircle size={30} className={"submission-icon"}*/}
                                                {/*                 onClick={() => setInfoDisplay(infoDisplay === 'none' ? 'block' : 'none')}/>*/}
                                                {/*        :*/}
                                                {/*        <Info size={30} className={"submission-icon"}*/}
                                                {/*              onClick={() => setInfoDisplay(infoDisplay === 'none' ? 'block' : 'none')}/>*/}
                                                {/*    }*/}
                                                {/*</div>*/}
                                            </div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                                <table className={"submission-table-lower"}>
                                    <tbody>
                                    <tr className={"submission-tr-lower"} style={{position: 'relative'}}>
                                        <td className={"submission-td-left-lower"}>Novelty:</td>
                                        <td className={"submission-td-right-lower"}>{manuscript.noveltyScore != null ? manuscript.noveltyScore : '?'}</td>
                                        <td style={{position: 'relative'}}>
                                            {infoDisplayNovelty === 'block' ?
                                                <XCircle size={'100%'} className={"score-icon"}
                                                         onClick={() => {
                                                             setInfoDisplayNovelty(infoDisplayNovelty === 'none' ? 'block' : 'none');
                                                             setInfoDisplayMethodology('none');
                                                             setInfoDisplayImpact('none');
                                                             setInfoDisplayOverall('none');
                                                         }}/>
                                                :
                                                <Info size={'100%'} className={"score-icon"}
                                                      onClick={() => {
                                                          setInfoDisplayNovelty(infoDisplayNovelty === 'none' ? 'block' : 'none');
                                                          setInfoDisplayMethodology('none');
                                                          setInfoDisplayImpact('none');
                                                          setInfoDisplayOverall('none');
                                                      }}/>
                                            }
                                            {displayBoxNovelty}
                                        </td>
                                    </tr>
                                    <tr className={"submission-tr-lower"} style={{position: 'relative'}}>
                                        <td className={"submission-td-left-lower"}>Methodology:</td>
                                        <td className={"submission-td-right-lower"}>{manuscript.methodScore != null ? manuscript.methodScore : '?'}</td>
                                        <td style={{position: 'relative'}}>
                                            {infoDisplayMethodology === 'block' ?
                                                <XCircle size={'100%'} className={"score-icon"}
                                                         onClick={() => {
                                                             setInfoDisplayMethodology(infoDisplayMethodology === 'none' ? 'block' : 'none');
                                                             setInfoDisplayNovelty('none');
                                                             setInfoDisplayImpact('none');
                                                             setInfoDisplayOverall('none');
                                                         }}/>
                                                :
                                                <Info size={'100%'} className={"score-icon"}
                                                      onClick={() => {
                                                          setInfoDisplayMethodology(infoDisplayMethodology === 'none' ? 'block' : 'none');
                                                          setInfoDisplayNovelty('none');
                                                          setInfoDisplayImpact('none');
                                                          setInfoDisplayOverall('none');
                                                      }}/>
                                            }
                                            {displayBoxMethodology}
                                        </td>
                                    </tr>
                                    <tr className={"submission-tr-lower"} style={{position: 'relative'}}>
                                        <td className={"submission-td-left-lower"}>Impact:</td>
                                        <td className={"submission-td-right-lower"}>{manuscript.impactScore != null ? manuscript.impactScore : '?'}</td>
                                        <td style={{position: 'relative'}}>
                                            {infoDisplayImpact === 'block' ?
                                                <XCircle size={'100%'} className={"score-icon"}
                                                         onClick={() => {
                                                             setInfoDisplayImpact(infoDisplayImpact === 'none' ? 'block' : 'none');
                                                             setInfoDisplayNovelty('none');
                                                             setInfoDisplayMethodology('none');
                                                             setInfoDisplayOverall('none');
                                                         }}/>
                                                :
                                                <Info size={'100%'} className={"score-icon"}
                                                      onClick={() => {
                                                          setInfoDisplayImpact(infoDisplayImpact === 'none' ? 'block' : 'none');
                                                          setInfoDisplayNovelty('none');
                                                          setInfoDisplayMethodology('none');
                                                          setInfoDisplayOverall('none');
                                                      }}/>
                                            }
                                            {displayBoxImpact}
                                        </td>
                                    </tr>
                                    {/*<td className={"submission-td-left-lower"}>Impact:</td>*/}
                                    {/*<td className={"submission-td-right-lower"}>{manuscript.impactScore != null ? manuscript.impactScore : '?'}</td>*/}
                                    {/*<td style={{position: 'relative'}}>*/}
                                    {/*    {infoDisplayImpact === 'block' ?*/}
                                    {/*        <XCircle size={'100%'} className={"score-icon"}*/}
                                    {/*                 onClick={() => {*/}
                                    {/*                     setInfoDisplayImpact(infoDisplayImpact === 'none' ? 'block' : 'none');*/}
                                    {/*                     setInfoDisplayNovelty('none');*/}
                                    {/*                     setInfoDisplayMethodology('none');*/}
                                    {/*                     setInfoDisplayOverall('none');*/}
                                    {/*                 }}/>*/}
                                    {/*        :*/}
                                    {/*        <Info size={'100%'} className={"score-icon"}*/}
                                    {/*              onClick={() => {*/}
                                    {/*                  setInfoDisplayImpact(infoDisplayImpact === 'none' ? 'block' : 'none');*/}
                                    {/*                  setInfoDisplayNovelty('none');*/}
                                    {/*                  setInfoDisplayMethodology('none');*/}
                                    {/*                  setInfoDisplayOverall('none');*/}
                                    {/*              }}/>*/}
                                    {/*    }*/}
                                    {/*    {displayBoxImpact}*/}
                                    {/*</td>*/}
                                    {/*</tr>*/}
                                    </tbody>
                                </table>
                                {/*{manuscript.impactConfidence != null &&*/}
                                {/*    <div className={"margin-top-1rem margin-bottom-1-rem"}>*/}
                                {/*        <div className={"export-score-text"}>Likelihood of your research influencing*/}
                                {/*            future studies*/}
                                {/*        </div>*/}
                                {/*        <div style={{marginTop: "1rem"}}>*/}
                                {/*            <DonutProgress value={manuscript.impactConfidence} size={100} strokeWidth={12}/>*/}
                                {/*        </div>*/}
                                {/*    </div>*/}
                                {/*}*/}
                                {manuscript.graphKey != null &&
                                    <div className="submission-card dashboard-row box-shadow max-width-400"
                                         style={{
                                             cursor:
                                                 manuscript.status === null || manuscript.status === "completed"
                                                     ? "pointer"
                                                     : "not-allowed",
                                         }}
                                         onClick={() => {
                                             if (manuscript.status === null || manuscript.status === "completed") {
                                                 navigate("/network?id=" + id)
                                             }
                                         }}>
                                        <MdOutlineHub size={30} className={"submission-low-card-icon"}/>
                                        <div className={"submission-title"}>Network View</div>
                                    </div>
                                }
                                {hasSimilarPapers &&
                                    <div className="submission-card dashboard-row box-shadow max-width-400"
                                         style={{
                                             cursor:
                                                 manuscript.status === null || manuscript.status === "completed"
                                                     ? "pointer"
                                                     : "not-allowed",
                                         }}
                                         onClick={() => {
                                             if (manuscript.status === null || manuscript.status === "completed") {
                                                 navigate("/similar-works?id=" + id)
                                             }
                                         }}>
                                        <Files size={30} className={"submission-low-card-icon"}/>
                                        <div className={"submission-title"}>View Similar Works</div>
                                    </div>
                                }
                                {/*<div*/}
                                {/*    className="submission-card dashboard-row box-shadow max-width-400"*/}
                                {/*    onClick={() => {*/}
                                {/*        if (manuscript.status === null || manuscript.status === "completed") {*/}
                                {/*            navigate("/impact-vs-field?id=" + id);*/}
                                {/*        }*/}
                                {/*    }}*/}
                                {/*    style={{*/}
                                {/*        cursor:*/}
                                {/*            manuscript.status === null || manuscript.status === "completed"*/}
                                {/*                ? "pointer"*/}
                                {/*                : "not-allowed",*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    <GiDropletSplash size={30} className={"submission-low-card-icon"}/>*/}
                                {/*    <div className={"submission-title"}>Impact vs. Field</div>*/}
                                {/*</div>*/}
                                {/*<div className="submission-card dashboard-row box-shadow max-width-400"*/}
                                {/*     onClick={() => navigate("/suggested-editorial-action?id=" + id)}>*/}
                                {/*    <Eraser size={30} className={"submission-low-card-icon"}/>*/}
                                {/*    <div className={"submission-title"}>Suggested Action</div>*/}
                                {/*</div>*/}
                                {manuscript.status === "completed" &&
                                    <div
                                        className="submission-card dashboard-row box-shadow max-width-400"
                                        onClick={() => {
                                            if (manuscript.status === null || manuscript.status === "completed") {
                                                navigate("/export-center?id=" + id);
                                            }
                                        }}
                                        style={{
                                            cursor:
                                                manuscript.status === null || manuscript.status === "completed"
                                                    ? "pointer"
                                                    : "not-allowed",
                                        }}
                                    >

                                        <ExternalLink size={30} className={"submission-low-card-icon"}/>
                                        <div className={"submission-title"}>Export Center</div>
                                    </div>
                                }
                                {existsInS3 &&
                                    <div
                                        className="submission-card dashboard-row box-shadow max-width-400"
                                        onClick={() => {
                                            downloadManuscript(id);
                                        }}
                                    >

                                        <Download size={30} className={"submission-low-card-icon"}/>
                                        <div className={"submission-title"}>Download Manuscript</div>
                                    </div>
                                }
                            </div>
                        }
                        <button onClick={() => navigate("/submissions")}
                                className={"back-button fade-in mobile-margin-top-1-2-rem"}
                        ><span className={"back-button-text"}>&larr;</span>
                        </button>
                        {(manuscript.status !== null && manuscript.status === "completed") ?
                            <div className={"user-account-link mobile-margin-top-1-2-rem margin-top-2-rem"} style={{color: red}}
                                 onClick={() => deleteSubmission(true)}
                            >
                                Delete Submission
                            </div>
                            :
                            <div className={"margin-bottom-1-rem"}></div>
                        }
                    </div>
            }
        </div>
    );
}

export default Submission;
